import { Loader2 } from 'lucide-react';
import { Link } from 'react-router';

/**
 * Stage 2 multi-WABA phone picker — presentational only (no fetch inside).
 *
 * Rendered by MetaConnectingPage when POST /meta/signup returns
 * `{ status: 'selection_required' }`, and reused by the CLI callback page
 * (Task 7). The parent owns the POST /meta/signup/select call; this
 * component just renders the candidate phones and reports the choice.
 *
 * Phones flagged `alreadyConnected` are disabled: active in *another*
 * workspace (spec Decision 6), or — in a reconnect picker — already connected
 * on another channel in *this* workspace (forking onto them 409s, AIT-163).
 * In ordinary connects, same-workspace numbers stay selectable as a reconnect.
 */
export interface PickerPhone {
  phoneNumberId: string;
  displayPhone: string;
  verifiedName: string;
  wabaName: string;
  alreadyConnected: boolean;
  // AIT-617: where the number is held. Optional so an older API response still
  // renders (falls back to the generic label).
  connectedElsewhere?: 'another_organization' | 'another_workspace' | 'this_workspace' | null;
}

const CONNECTED_ELSEWHERE_LABEL = {
  // AIT-689: the reader may be an end client; never name an organization.
  another_organization: 'Connected to another service',
  another_workspace: 'Connected in another workspace',
  this_workspace: 'Connected on another channel',
} as const;

export function WabaPhonePicker({
  phones,
  submitting,
  onSelect,
  reconnect,
  backTo,
}: {
  phones: PickerPhone[];
  submitting: boolean;
  onSelect: (phoneNumberId: string) => void;
  // Reconnect-in-place (AIT-163): when present, picking the target's OWN number
  // reconnects it in place; picking a DIFFERENT number creates a brand-new
  // channel (with its own token) and leaves the reconnected channel untouched.
  // We surface that as an inline hint — there is no separate opt-in action.
  reconnect?: { targetPhoneNumberId: string };
  // AIT-547 follow-up: a way out of the picker. When every number is already
  // connected there is nothing to click; the caller decides where "back" is
  // (the invite picker or the workspace channels page).
  backTo?: { href: string; label: string };
}) {
  if (submitting) {
    return (
      <div className="flex flex-col items-center justify-center gap-4 py-24">
        <Loader2 className="size-8 animate-spin text-muted-foreground" />
        <p className="text-lg font-medium">Connecting your channel…</p>
      </div>
    );
  }

  return (
    <div
      role="group"
      aria-label="Choose a WhatsApp number to connect"
      className="mx-auto flex w-full max-w-md flex-col gap-6 px-4 py-24"
    >
      <div className="flex flex-col gap-1 text-center">
        <h1 className="text-lg font-semibold">Choose a Number to Connect</h1>
        <p className="text-sm text-muted-foreground">
          Your Meta account manages more than one WhatsApp number. Pick the one
          to connect to this workspace.
        </p>
      </div>
      <ul className="flex flex-col gap-2">
        {phones.map((p) => {
          // Reconnect (AIT-163): a number that DIFFERS from the target's own
          // number creates a new channel rather than reconnecting this one.
          // Disabled (already-connected) rows never show the fork hint — it
          // would invite a click that can only 409.
          const forksNewChannel =
            !!reconnect &&
            !p.alreadyConnected &&
            p.phoneNumberId !== reconnect.targetPhoneNumberId;
          return (
            <li key={p.phoneNumberId} className="flex flex-col gap-2">
              <button
                type="button"
                disabled={p.alreadyConnected || submitting}
                onClick={() => onSelect(p.phoneNumberId)}
                className="flex w-full items-center gap-4 rounded-xl border border-border bg-card p-4 text-left transition hover:border-foreground/30 hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:border-border disabled:hover:bg-card"
              >
                <span className="flex min-w-0 flex-col">
                  <span className="font-medium">
                    {p.displayPhone} · {p.wabaName}
                  </span>
                  <span className="truncate text-sm text-muted-foreground">
                    {p.verifiedName}
                  </span>
                  {forksNewChannel ? (
                    <span className="truncate text-xs text-muted-foreground">
                      Creates a new channel with its own token
                    </span>
                  ) : null}
                </span>
                {p.alreadyConnected ? (
                  <span className="ml-auto shrink-0 text-xs text-muted-foreground">
                    {p.connectedElsewhere
                      ? CONNECTED_ELSEWHERE_LABEL[p.connectedElsewhere]
                      : 'Already connected'}
                  </span>
                ) : null}
              </button>
            </li>
          );
        })}
      </ul>
      {backTo ? (
        <Link
          to={backTo.href}
          className="inline-flex w-full items-center justify-center rounded-[100px] border border-border px-8 py-3 text-sm font-medium transition hover:bg-accent"
        >
          {backTo.label}
        </Link>
      ) : null}
    </div>
  );
}
